import { __rest } from "tslib";
import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, mergeProps as _mergeProps, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vShow as _vShow, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue";
const _withScopeId = n => (_pushScopeId("data-v-29e3cda2"), n = n(), _popScopeId(), n);
const _hoisted_1 = { class: "flex flex-col h-full *: pointer-events-none" };
const _hoisted_2 = { class: "relative h-full isolate bg-black lg:bg-transparent" };
const _hoisted_3 = { class: "Slide__Container relative flex justify-center items-center w-full h-full lg:h-auto" };
const _hoisted_4 = { class: "Slide__Grid relative lg:absolute lg:inset-0 grid" };
const _hoisted_5 = { class: "StorySlider__UI h-full pointer-events-none" };
const _hoisted_6 = { class: "relative flex justify-center items-center h-full" };
const _hoisted_7 = { class: "text-desktop-cap text-black lg:text-white" };
import { ref, computed, watch, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { onKeyDown } from "@vueuse/core";
import { getCurrentContent, muxManifestUrl } from "@/config/content";
import { MEDIA_CLOSING_DURATION } from "@/config/constants";
import useGSAP from "@/composables/useGSAP";
import useLocalTransition from "@/composables/useLocalTransition";
import { setFPS } from "@/utils/frameToTime";
import Viewport from "@/store/modules/Viewport";
import AudioManager from "@webgl/core/audio/AudioManager";
export default /*#__PURE__*/ _defineComponent({
    __name: 'StorySlider',
    props: {
        show: { type: Boolean },
        loop: { type: Boolean, default: true }
    },
    emits: ["close"],
    setup(__props, { emit: __emit }) {
        const SWIPE_SOUNDS = ["UI_ASSET_SWIPE-00", "UI_ASSET_SWIPE-01", "UI_ASSET_SWIPE-02"];
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const gsap = useGSAP();
        const frames = setFPS(60);
        const show = useLocalTransition(() => props.show);
        const { content } = getCurrentContent()[0];
        const randBorder = gsap.utils.random(["solid", "paper"], true);
        const slides = computed(() => content.map(({ image, video, text }) => ({
            picture: Object.assign(Object.assign({}, image), { src: [`${Viewport.isMobile ? image.src.replace("/assets/content/generated/", "/assets/content/generated-mobile/") : image.src}.avif`, `${image.src}.webp`], border: randBorder() }),
            video: video ? Object.assign(Object.assign({}, video), { src: (video === null || video === void 0 ? void 0 : video.src) ? muxManifestUrl(video.src, Viewport.isMobile ? "?max_resolution=720p" : "") : "", poster: `${Viewport.isMobile ? image.src.replace("/assets/content/generated/", "/assets/content/generated-mobile/") : image.src}.avif` }) : undefined,
            card: {
                title: t(text.titlekey),
                description: t(text.textkey),
                caption: t(text.captionkey),
            }
        })));
        const hasNav = computed(() => slides.value.length > 1);
        const { isDesktop, isMobile } = toRefs(Viewport);
        const root = ref(null);
        const slideEls = ref([]);
        const isAppeared = ref(false);
        const minIndex = ref(0);
        const maxIndex = computed(() => slides.value.length - 1);
        const currentIndex = ref(0);
        const setCurrentIndex = (index) => {
            const modifier = gsap.utils[props.loop ? "wrap" : "clamp"];
            currentIndex.value = modifier(minIndex.value, maxIndex.value + +props.loop, index);
        };
        const currentSlide = computed(() => slides.value[currentIndex.value]);
        const playSwipeSound = () => AudioManager.playUI(gsap.utils.random(SWIPE_SOUNDS), 0, 1.0, true);
        function prev() {
            setCurrentIndex(currentIndex.value - 1);
        }
        function next() {
            setCurrentIndex(currentIndex.value + 1);
        }
        function close() {
            AudioManager.playUI("UI_SECONDARY_CLICK");
            emit("close");
        }
        const randRotation = gsap.utils.random(0, 3, true);
        const slideRandomRotation = (index) => randRotation() * (index % 2 === 0 ? -1 : 1);
        const exposure = (amount) => {
            if (Viewport.isMobile)
                return {};
            return { filter: `brightness(${amount})` };
        };
        const slidesTweenVars = ref(Array(slides.value.length).fill({
            xPercent: 0,
            yPercent: 0,
            rotation: 0,
            opacity: 1,
            brightness: 1,
            zIndex: 0,
        }));
        const CARD_TRANSITION_ENTER_DELAY = frames(10);
        const cardTransitionDelay = ref(frames(40) + CARD_TRANSITION_ENTER_DELAY);
        function onSlideChange() {
            if (!root.value)
                return;
            const $ = gsap.utils.selector(root.value);
            const tl = gsap.timeline({
                defaults: {
                    duration: frames(40),
                    ease: "ease.out"
                }
            });
            slidesTweenVars.value.forEach((_a, i) => {
                var { brightness, zIndex } = _a, vars = __rest(_a, ["brightness", "zIndex"]);
                const slideEl = slideEls.value[i];
                tl.to(slideEl, Object.assign(Object.assign({}, vars), { onStart: () => {
                        onBeforeSlideChange(i);
                    }, onComplete: () => {
                        const { zIndex } = slidesTweenVars.value[i];
                        zIndex < 0 && gsap.set(slideEl, { zIndex });
                        onAfterSlideChange(i);
                    } }), 0);
                if (zIndex > 0) {
                    tl.set(slideEl, { zIndex }, 0);
                }
                if (brightness > 1) {
                    tl.to(slideEl, {
                        keyframes: [
                            Object.assign(Object.assign({}, exposure(brightness)), { duration: frames(30), ease: "ease.out" }),
                            Object.assign(Object.assign({}, exposure(1)), { duration: frames(30), ease: "quart.inOut" }),
                        ],
                        ease: "none",
                    }, 0);
                }
                else {
                    tl.to(slideEl, Object.assign({}, exposure(brightness)), 0);
                }
            });
        }
        function onBeforeSlideChange(index) {
            const slideEl = slideEls.value[index];
            const video = slideEl === null || slideEl === void 0 ? void 0 : slideEl.querySelector("video");
            if (!video)
                return;
            // pause video
            video.pause();
        }
        function onAfterSlideChange(index) {
            if (index !== currentIndex.value)
                return;
            const slideEl = slideEls.value[index];
            const video = slideEl.querySelector("video");
            if (!video)
                return;
            // autoplay video
            video.play();
        }
        function onCardEnter(el, onComplete) {
            // @TODO: mobile animation
            if (isMobile.value)
                return onComplete();
            const tl = gsap.timeline({
                onComplete,
                defaults: {
                    duration: frames(40),
                    ease: "ease.out"
                }
            });
            tl
                .set(el, { xPercent: 110, y: 120, rotation: 20 })
                .to(el, {
                motionPath: {
                    path: [{ xPercent: 55, y: 60 }, { xPercent: 0, y: 0 }],
                    curviness: 2,
                },
                rotation: 4
            }, isAppeared.value ? 0 : frames(40));
        }
        function onCardAfterAppear() {
            isAppeared.value = true;
            cardTransitionDelay.value = CARD_TRANSITION_ENTER_DELAY;
        }
        function onCardLeave(el, onComplete) {
            // @TODO: mobile animation
            if (isMobile.value)
                return onComplete();
            gsap.set(el, Object.assign({}, exposure(1)));
            const tl = gsap.timeline({
                onComplete,
                defaults: {
                    duration: frames(40),
                    ease: "ease.out"
                }
            });
            tl.to(el, Object.assign({ motionPath: {
                    path: [{ xPercent: 0, y: 0 }, { xPercent: 55, y: 60 }, { xPercent: 110, y: 120 }],
                    curviness: 2,
                    fromCurrent: false,
                }, rotation: 20 }, exposure(0.5)), 0);
        }
        function onBeforeEnter() {
            slidesTweenVars.value.forEach((vars, i) => {
                slidesTweenVars.value[i] = Object.assign(Object.assign({}, vars), { rotation: isDesktop.value ? slideRandomRotation(i) : 0, zIndex: slides.value.length - i });
            });
        }
        function onEnter(el, onComplete) {
            const $ = gsap.utils.selector(el);
            const stagger = frames(3);
            const tl = gsap.timeline({
                onStart: () => {
                    AudioManager.playUI("UI_ASSETS_TRANSITION");
                },
                onComplete,
                defaults: {
                    duration: frames(50),
                    ease: "ease.out"
                }
            });
            const slides = $(".Slide");
            const pictures = $(".Slide__Picture");
            slides.forEach((slide, i) => {
                const revI = slides.length - 1 - i;
                const picture = pictures[i];
                const delay = revI * stagger;
                const { zIndex, rotation } = slidesTweenVars.value[i];
                gsap.set(slide, Object.assign({ rotation: isDesktop.value ? "random(15,45)" : 0, opacity: isDesktop.value || i === currentIndex.value ? 1 : 0, zIndex }, exposure(30)));
                if (isDesktop.value) {
                    let { y } = picture.getBoundingClientRect();
                    y = Viewport.windowHeight - y;
                    gsap.set(slide, { x: "random(-24,24)", y });
                    tl.to(slide, { x: 0, y: 0, rotation }, delay);
                }
                tl.to(slide, Object.assign(Object.assign({}, exposure(1)), { onComplete: () => onAfterSlideChange(i) }), delay + frames(10));
            });
            tl
                .from($(".StorySlider__Backdrop"), {
                opacity: 0,
                duration: frames(40)
            }, 0)
                .from($(".StorySlider__UI"), {
                opacity: 0,
                duration: frames(30)
            }, 0);
            if (hasNav.value) {
                tl.from($(".SliderNav"), {
                    yPercent: 100,
                    duration: frames(30)
                }, ">");
            }
        }
        function onBeforeLeave() {
            onBeforeSlideChange(currentIndex.value);
        }
        function onLeave(el, onComplete) {
            const tl = gsap.timeline({ onComplete });
            tl.to(el, {
                opacity: 0,
                duration: MEDIA_CLOSING_DURATION / 1000,
                ease: "ease.out"
            });
        }
        watch(currentIndex, (index) => {
            playSwipeSound();
            const newVars = [];
            for (let i = 0; i < slides.value.length; i++) {
                let { xPercent, yPercent, rotation, opacity, brightness, zIndex } = slidesTweenVars.value[i];
                const isOut = i < index;
                const isCurrent = i === index;
                const revIndex = slides.value.length - i;
                const outRandOffset = gsap.utils.random(42.5, 50, true);
                const outRandRotation = gsap.utils.random(15, 45, true);
                opacity = isMobile.value && isOut ? 0 : 1;
                brightness = isOut ? 0.5 : isCurrent ? 2 : 1;
                zIndex = isDesktop && isOut ? -revIndex : revIndex;
                if (isDesktop.value) {
                    if (isOut) {
                        xPercent = xPercent === 0 ? outRandOffset() * (i % 3 === 2 ? 1 : -1) : xPercent;
                        yPercent = yPercent === 0 ? outRandOffset() * (i % 3 === 1 ? 1 : -1) : yPercent;
                        rotation = Math.abs(rotation) < 5 ? outRandRotation() * (i % 3 === 2 ? 1 : -1) : rotation;
                    }
                    else {
                        xPercent = 0;
                        yPercent = 0;
                        rotation = slideRandomRotation(i);
                    }
                }
                else {
                    rotation = 0;
                }
                newVars[i] = { xPercent, yPercent, rotation, opacity, brightness, zIndex };
            }
            slidesTweenVars.value = newVars;
        });
        watch(slidesTweenVars, onSlideChange);
        onKeyDown(["ArrowLeft", "PageUp"], prev);
        onKeyDown(["ArrowRight", "PageDown"], next);
        onKeyDown(["Escape"], close);
        return (_ctx, _cache) => {
            const _component_UIPictureVideo = _resolveComponent("UIPictureVideo");
            const _component_UIPicture = _resolveComponent("UIPicture");
            const _component_UICard = _resolveComponent("UICard");
            const _component_UIBtnClose = _resolveComponent("UIBtnClose");
            const _component_UIPrevNext = _resolveComponent("UIPrevNext");
            const _directive_cursor = _resolveDirective("cursor");
            return (_openBlock(), _createBlock(_Transition, {
                css: false,
                onBeforeEnter: onBeforeEnter,
                onEnter: onEnter,
                onBeforeLeave: onBeforeLeave,
                onLeave: onLeave
            }, {
                default: _withCtx(() => [
                    _withDirectives(_createElementVNode("div", {
                        ref_key: "root",
                        ref: root,
                        class: _normalizeClass(["StorySlider absolute inset-0 z-1 grid *:row-start-1 *:col-start-1 pointer-events-auto", { hasNav: hasNav.value }])
                    }, [
                        _createElementVNode("div", {
                            class: "StorySlider__Backdrop size-full bg-true-black/60",
                            onClick: close
                        }),
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("div", _hoisted_2, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, ({ picture, video }, i) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        ref_for: true,
                                        ref: (el) => (slideEls.value[i] = el),
                                        key: i,
                                        class: "Slide absolute inset-0 flex justify-center items-center h-full"
                                    }, [
                                        _createElementVNode("div", _hoisted_3, [
                                            video
                                                ? _withDirectives((_openBlock(), _createBlock(_component_UIPictureVideo, _mergeProps({
                                                    key: 0,
                                                    class: "Slide__Picture size-full lg:size-auto lg:min-w-0 lg:min-h-0 lg:max-w-full lg:max-h-full pointer-events-auto"
                                                }, Object.assign(Object.assign({}, video), { border: _unref(isDesktop) ? picture.border : 'none' }), {
                                                    onClick: _cache[0] || (_cache[0] = ($event) => (_unref(isDesktop) && next()))
                                                }), null, 16)), [
                                                    [
                                                        _directive_cursor,
                                                        void 0,
                                                        void 0,
                                                        { hover: true }
                                                    ]
                                                ])
                                                : _withDirectives((_openBlock(), _createBlock(_component_UIPicture, _mergeProps({
                                                    key: 1,
                                                    class: "Slide__Picture size-full lg:size-auto lg:min-w-0 lg:min-h-0 lg:max-w-full lg:max-h-full pointer-events-auto"
                                                }, Object.assign(Object.assign({}, picture), { border: _unref(isDesktop) ? picture.border : 'none' }), {
                                                    onClick: _cache[1] || (_cache[1] = ($event) => (_unref(isDesktop) && next()))
                                                }), null, 16)), [
                                                    [
                                                        _directive_cursor,
                                                        void 0,
                                                        void 0,
                                                        { hover: true }
                                                    ]
                                                ])
                                        ])
                                    ]));
                                }), 128))
                            ]),
                            _createElementVNode("div", _hoisted_4, [
                                _createVNode(_Transition, {
                                    css: false,
                                    appear: "",
                                    onEnter: onCardEnter,
                                    onAfterAppear: onCardAfterAppear,
                                    onLeave: onCardLeave
                                }, {
                                    default: _withCtx(() => [
                                        (_openBlock(), _createElementBlock("div", {
                                            key: currentIndex.value,
                                            class: "Slide__Card self-start flex items-end lg:max-h-full lg:-ml-48 lg:-mt-32 lg:pr-32 lg:pb-32"
                                        }, [
                                            _createVNode(_component_UICard, _mergeProps({ class: "backface-hidden pointer-events-auto" }, currentSlide.value.card, {
                                                delay: cardTransitionDelay.value,
                                                folded: _unref(isDesktop)
                                            }), null, 16, ["delay", "folded"])
                                        ]))
                                    ]),
                                    _: 1
                                })
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, [
                                _createVNode(_component_UIBtnClose, {
                                    class: "!absolute top-20 right-20 lg:top-24 lg:right-24 justify-self-end pointer-events-auto",
                                    onClick: close
                                }),
                                (hasNav.value)
                                    ? (_openBlock(), _createBlock(_component_UIPrevNext, {
                                        key: 0,
                                        class: "SliderNav absolute bottom-0 gap-32 w-full lg:w-auto px-20 pb-20 lg:pb-32",
                                        modelValue: currentIndex.value,
                                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((currentIndex).value = $event)),
                                        min: minIndex.value,
                                        max: maxIndex.value,
                                        loop: _ctx.loop,
                                        theme: _unref(isDesktop) ? 'light' : 'dark'
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("span", _hoisted_7, [
                                                _createElementVNode("span", null, _toDisplayString(currentIndex.value + 1), 1),
                                                _createTextVNode("/" + _toDisplayString(maxIndex.value + 1), 1)
                                            ])
                                        ]),
                                        _: 1
                                    }, 8, ["modelValue", "min", "max", "loop", "theme"]))
                                    : _createCommentVNode("", true)
                            ])
                        ])
                    ], 2), [
                        [_vShow, _unref(show)]
                    ])
                ]),
                _: 1
            }));
        };
    }
});
